'use client'

import { LineCopy } from '@qr-front/icons/line-copy'
import { LineDownload } from '@qr-front/icons/line-download'
import { LineShareAndroid } from '@qr-front/icons/line-share-android'
import { theme } from '@qr-front/ui'
import { Button } from '@qr-front/ui/components/Button'
import {
	Dialog,
	DialogDescription,
	DialogTitle,
} from '@qr-front/ui/components/Dialog'
import { DialogContent } from '@qr-front/ui/components/Dialog'
import { cn, getVersionForClone } from '@qr-front/ui/utils'
import { userSettingsApi } from 'api/userSettings'
import { FocusRemover } from 'components/FocusRemover'
import { showNotification } from 'components/Notification'
import { qrListAtom } from 'features/library/client'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useState } from 'react'
import { UserMarketingFlowStatusEnum } from 'types/subscription'
import {
	buildQrMarkupWithoutFrame,
	DOWNLOAD_QR_ACTION_TYPES,
	downloadQRCode,
} from 'utils/downloadQrCode'
import { replaceQrColors } from 'utils/replaceQrColors'
import { buildUrl } from 'utils/urlUtils'

import { NEW_USER_COOKIE } from '@/constants'
import { TQrDesign } from '@/generator/client'
import { QR_TYPE, qrCodesApi } from '@/generator/shared'
import { userAtom } from '@/global-state'
import { copyToClipboard, shareViaWebShare } from '@/utils'

import { getCookieIsomorphic } from '../../../../../utils/shared'
import { captureBuilderEvent } from '../../../../generator/builderAnalytics'
import {
	downloadQrDialogAtom,
	downloadQrTypeAtom,
	qrCodeAtom,
	qrDownloadCompletedAtom,
} from '../dialogStates'
import QrCodeIllustration from './QrCodeIllustration'
import { SelectDownloadType } from './SelectDownloadType'

// MYQRCODE-10778 AC#13: qr_downloaded's file_format is svg/png/jpeg — the
// button's own action-type strings (downloadAsSvg/...) aren't the registry's
// vocabulary, so map them rather than send the internal identifier.
const DOWNLOAD_ACTION_TYPE_TO_FILE_FORMAT: Record<
	DOWNLOAD_QR_ACTION_TYPES,
	string
> = {
	downloadAsSvg: 'svg',
	downloadAsPng: 'png',
	downloadAsJpg: 'jpeg',
}

export const DownloadQrDialog = () => {
	const t = useTranslations()
	const [isCopied, setIsCopied] = useState(false)
	const qrList = useAtomValue(qrListAtom)
	const [user, setUser] = useAtom(userAtom)
	const [isOpen, setIsOpen] = useAtom(downloadQrDialogAtom)
	const [qrCode, setQrCode] = useAtom(qrCodeAtom)
	const [selectedType, setSelectedType] = useAtom(downloadQrTypeAtom)
	const [isNewUser, setIsNewUser] = useState<boolean>(false)
	const setDownloadCompleted = useSetAtom(qrDownloadCompletedAtom)

	const [qrMarkup, setQrMarkup] = useState<string>('')

	const searchParams = useSearchParams()

	const url = buildUrl(`qr-download/${qrCode?.uri}`)

	const qrId = searchParams.get('id')
	const openModal = searchParams.get('open-modal')
	const isDownloadModal = openModal === 'qr-code-download'
	const isWifiQrType = qrCode?.content?.type === QR_TYPE.Wifi

	const shouldShowForMarketingFlow =
		!!user && user.marketingFlowState === undefined

	const shouldAutoOpen = isNewUser || shouldShowForMarketingFlow

	useEffect(() => {
		getCookieIsomorphic(NEW_USER_COOKIE).then((newUserCookie) =>
			setIsNewUser(newUserCookie === 'true'),
		)
	}, [])

	useEffect(() => {
		if (isDownloadModal || shouldAutoOpen) {
			const qrCodeId = qrId || (shouldAutoOpen && qrList[0]?.id)
			if (!qrCodeId) return
			qrCodesApi.getQr(qrCodeId as string).then(async (qr) => {
				setQrCode(qr.data)
				setIsOpen(true)
			})
		}
	}, [isDownloadModal, qrId, setIsOpen, setQrCode, shouldAutoOpen, qrList])

	useEffect(() => {
		if (isOpen && qrCode && shouldShowForMarketingFlow && user) {
			userSettingsApi
				.setFirstLoginMarketing(UserMarketingFlowStatusEnum.LOGGEDIN)
				.then(() => {
					setUser({
						...user,
						marketingFlowState:
							UserMarketingFlowStatusEnum.LOGGEDIN,
					})
				})
		}
	}, [isOpen, qrCode, shouldShowForMarketingFlow, user, setUser])

	useEffect(() => {
		if (qrCode?.qrDesign) {
			const updatedQrCode = {
				...qrCode,
				qrDesign: replaceQrColors(
					qrCode.qrDesign as TQrDesign,
					theme.colors.purple,
				),
			}
			buildQrMarkupWithoutFrame(updatedQrCode).then((qrMarkup) => {
				setQrMarkup(qrMarkup)
			})
		}
	}, [qrCode])

	useEffect(() => {
		if (isDownloadModal && qrId && isOpen) {
			const newSearchParams = new URLSearchParams(searchParams)
			newSearchParams.delete('open-modal')
			newSearchParams.delete('id')
			newSearchParams.delete('qr_creation')
			const newUrl = `${window.location.pathname}${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''}`
			window.history.replaceState(null, '', newUrl)
		}
	}, [isDownloadModal, qrId, searchParams, isOpen])

	const onCopy = useCallback(async () => {
		if (!url) return

		if (!(await copyToClipboard(url))) {
			showNotification(t('public__generic__unexpected_error'), {
				type: 'error',
			})
			return
		}

		setIsCopied(true)
		setTimeout(() => {
			setIsCopied(false)
		}, 3000)
	}, [url, t])

	const onShare = async () => {
		const result = await shareViaWebShare({
			title: 'QR Code',
			text: 'QR Code',
			url,
		})

		if (result === 'unsupported') {
			await onCopy()
			return
		}

		if (result === 'error') {
			showNotification(t('public__generic__unexpected_error'), {
				type: 'error',
			})
		}
	}

	const onDownload = async (type: DOWNLOAD_QR_ACTION_TYPES) => {
		if (!qrCode) return
		try {
			await downloadQRCode(qrCode, type)
			captureBuilderEvent('qr_downloaded', {
				qr_type: qrCode.content.type,
				file_format: DOWNLOAD_ACTION_TYPE_TO_FILE_FORMAT[type],
			})
			setDownloadCompleted(true)
			setIsOpen(false)
		} catch {
			showNotification(t('public__generic__unexpected_error'), {
				type: 'error',
			})
		}
	}

	const onDownloadTypeChange = (type: DOWNLOAD_QR_ACTION_TYPES) => {
		setSelectedType(type)
	}

	const onOpenChange = (open: boolean) => {
		setIsOpen(open)
		if (!open) {
			setQrCode(null)
		}
	}

	if (!qrCode) return null

	return (
		<Dialog open={isOpen} onOpenChange={onOpenChange}>
			<DialogContent
				closeButtonLabel={t('ui__popup__close_button')}
				className="w-full p-0 tablet:w-[500px]"
			>
				<FocusRemover />
				<QrCodeIllustration qrMarkup={qrMarkup} />
				<div className="relative z-10 flex flex-col items-center gap-6 rounded-[0px_0px_10px_10px] bg-white p-8 shadow-generator">
					<div className="flex flex-col items-center gap-2">
						<DialogTitle className="text-center">
							{t(
								'dashboard__qr_table__qr_card__download_qr_code_title',
							)}
						</DialogTitle>
						<DialogDescription className="text-center">
							{t(
								'dashboard__qr_table__qr_card__download_qr_code_description',
							)}
						</DialogDescription>
					</div>
					<div className="flex w-full flex-col gap-4">
						<SelectDownloadType
							type={selectedType}
							onChange={(value: string) =>
								onDownloadTypeChange(
									value as DOWNLOAD_QR_ACTION_TYPES,
								)
							}
						/>
						<div className="flex w-full gap-4">
							<Button
								className="flex-1"
								onClick={() => onDownload(selectedType)}
							>
								<LineDownload width={16} height={16} />
								{t('dashboard__qr_table__qr_card__download')}
							</Button>
							<Button
								visual="secondary"
								size="icon"
								onClick={onShare}
							>
								<LineShareAndroid width={16} height={16} />
							</Button>
						</div>
						{!isWifiQrType && (
							<div
								className={cn(
									'mt-2 flex h-10 cursor-pointer items-center gap-2 border border-solid border-border-grey bg-white px-4 py-2',
									getVersionForClone({
										base: 'rounded-8',
										clone1: 'rounded-full',
									}),
								)}
								onClick={onCopy}
							>
								<span className="type-body-small flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
									{url}
								</span>
								<LineCopy width={16} height={16} />
							</div>
						)}
						<span
							className={cn(
								'type-body-small -mt-2 flex w-full text-center text-green transition-opacity duration-300 ease-in-out word-break-fix',
								!isCopied && 'hidden',
							)}
						>
							{t(
								'generator__share_qr_dialog__success_copy_button',
							)}
						</span>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
